/**
 * AccountSettings — AI-model voorkeur (en voor admins de quota's).
 *
 * De `qa_live_`-sleutel per gebruiker is vervallen: de aanmelding bij
 * Anthropic staat per workspace in Instellingen → AI (`AiAuthSettings`).
 *
 * Geëxtraheerd uit `SettingsPage.tsx::AccountTab` zodat hetzelfde
 * component zowel binnen de oude `/settings`-pagina als binnen de
 * nieuwe Settings-dropdown-modal werkt zonder duplicatie.
 */
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ApiError, api } from "../api/client";
import { isAdminOfWorkspace, useSession } from "../state/session";
import { ld, pill } from "./ledgerUi";
import { QuotasSettings } from "./QuotasSettings";

interface AiModel {
  id: string;
  label: string;
  description: string;
  recommended?: boolean;
}

export function AccountSettings() {
  const { t } = useTranslation();
  // Admin-detectie zodat de admin-only Quotas-sectie alleen voor
  // admins onder de account-velden verschijnt — verzoek 2026-05-21:
  // Quotas verhuisd uit eigen modal naar onderaan Account.
  const me = useSession((s) => s.me);
  const workspaceId = useSession((s) => s.workspaceId);
  const isAdmin = isAdminOfWorkspace(me, workspaceId);
  const [aiModelPref, setAiModelPref] = useState<string | null>(null);
  const [aiEffortPref, setAiEffortPref] = useState<"low" | "medium" | "high" | null>(null);
  const [aiModels, setAiModels] = useState<AiModel[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      const status = await api.getUserSettings();
      setAiModelPref(status.ai_model_preference);
      setAiEffortPref(status.ai_effort_preference ?? null);
    } catch (err) {
      setError(explain(err));
    }
  }, []);

  useEffect(() => {
    void refresh();
    // Models-catalog is static for the lifetime of a session; one fetch.
    api
      .listAiModels()
      .then((res) => setAiModels(res.models))
      .catch(() => setAiModels([]));
  }, [refresh]);

  const onSelectModel = async (id: string) => {
    setBusy(true);
    setError(null);
    setInfo(null);
    try {
      // Empty string from the dropdown's "default" option clears the preference.
      const status = await api.setAiModel(id || null);
      setAiModelPref(status.ai_model_preference);
      const label = aiModels.find((m) => m.id === id)?.label;
      setInfo(
        id
          ? t("settings.account.modelSaved", { label: label ?? id })
          : t("settings.account.modelReset"),
      );
    } catch (err) {
      setError(explain(err));
    } finally {
      setBusy(false);
    }
  };

  const onSelectEffort = async (value: string) => {
    setBusy(true);
    setError(null);
    setInfo(null);
    try {
      const effort = value === "low" || value === "medium" || value === "high" ? value : null;
      const status = await api.setAiEffort(effort);
      setAiEffortPref(status.ai_effort_preference ?? null);
      setInfo(t("settings.account.effortSaved"));
    } catch (err) {
      setError(explain(err));
    } finally {
      setBusy(false);
    }
  };

  const recommended = aiModels.find((m) => m.recommended);
  const selectedModel = aiModels.find((m) => m.id === aiModelPref);

  return (
    <div className="flex max-w-2xl flex-col gap-8">
      {/* AI-model voorkeur — gebruikt door "Vraag Claude" in de editor */}
      <section>
        <h3 className={ld.sectionTitle}>{t("settings.account.aiModelSectionTitle")}</h3>
        <p className={ld.sectionIntro}>{t("settings.account.aiModelDescription")}</p>

        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <div>
            <label htmlFor="account-ai-model" className={ld.label}>
              {t("settings.account.modelLabel")}
            </label>
            <select
              id="account-ai-model"
              className={ld.input}
              value={aiModelPref ?? ""}
              onChange={(e) => void onSelectModel(e.target.value)}
              disabled={busy || aiModels.length === 0}
              {...(selectedModel ? { "aria-describedby": "account-ai-model-hint" } : {})}
            >
              <option value="">
                {t("settings.account.modelDefault")}
                {recommended ? ` (${recommended.label})` : ""}
              </option>
              {aiModels.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.label}
                  {m.recommended ? ` — ${t("settings.account.modelRecommended")}` : ""}
                </option>
              ))}
            </select>
            {/* De uitleg bij het gekozen model: snelheid tegenover kwaliteit. */}
            {selectedModel && (
              <p id="account-ai-model-hint" className={ld.hint}>
                {selectedModel.description}
              </p>
            )}
          </div>
          <div>
            <label htmlFor="account-ai-effort" className={ld.label}>
              {t("settings.account.effortLabel")}
            </label>
            <select
              id="account-ai-effort"
              aria-describedby="account-ai-effort-hint"
              className={ld.input}
              value={aiEffortPref ?? ""}
              onChange={(e) => void onSelectEffort(e.target.value)}
              disabled={busy}
            >
              <option value="">{t("settings.account.effortDefault")}</option>
              <option value="low">{t("settings.account.effortLow")}</option>
              <option value="medium">{t("settings.account.effortMedium")}</option>
              <option value="high">{t("settings.account.effortHigh")}</option>
            </select>
            <p id="account-ai-effort-hint" className={ld.hint}>
              {t("settings.account.effortDescription")}
            </p>
          </div>
        </div>

        <div aria-live="polite">
          {error && (
            <p role="alert" className={`mt-4 ${ld.error}`}>
              {error}
            </p>
          )}
          {info && <p className={`mt-4 ${ld.success}`}>{info}</p>}
        </div>

        <p className="mt-4 text-pretty text-(--ld-muted) text-[13px] leading-5">
          {t("settings.account.aiNote")}
        </p>
      </section>

      {/* Admin-sectie — alleen voor workspace-admins. Quotas zit hier
          ingebed i.p.v. als eigen dropdown-item zodat alle persoonlijke +
          admin-instellingen op één plek staan. */}
      {isAdmin && workspaceId && (
        <section className="border-(--ld-rule) border-t pt-8">
          <div className="flex items-center gap-2">
            <h3 className={ld.sectionTitle}>{t("settings.quotas.sectionTitle")}</h3>
            <span className={pill.neutral}>{t("app.header.settingsMenuAdminGroup")}</span>
          </div>
          <QuotasSettings workspaceId={workspaceId} />
        </section>
      )}
    </div>
  );
}

function explain(err: unknown): string {
  if (err instanceof ApiError) {
    return err.body?.detail ?? err.body?.title ?? `HTTP ${err.status}`;
  }
  if (err instanceof Error) {
    return err.message;
  }
  return String(err);
}
